import wavEncoder from "wav-encoder";

/**
 * Checks if an ArrayBuffer contains a standard RIFF/WAVE container header.
 */
export function isWavAudio(buffer: ArrayBuffer): boolean {
  if (!buffer || buffer.byteLength < 44) return false;
  const bytes = new Uint8Array(buffer, 0, 12);
  return (
    bytes[0] === 0x52 && // 'R'
    bytes[1] === 0x49 && // 'I'
    bytes[2] === 0x46 && // 'F'
    bytes[3] === 0x46 && // 'F'
    bytes[8] === 0x57 && // 'W'
    bytes[9] === 0x41 && // 'A'
    bytes[10] === 0x56 && // 'V'
    bytes[11] === 0x45 // 'E'
  );
}

/**
 * Resamples a Float32Array from sourceSampleRate to targetSampleRate using linear interpolation.
 */
export function resamplePcm(
  samples: Float32Array,
  sourceSampleRate: number,
  targetSampleRate: number
): Float32Array {
  if (sourceSampleRate === targetSampleRate || samples.length === 0) {
    return samples;
  }
  const ratio = sourceSampleRate / targetSampleRate;
  const newLength = Math.round(samples.length / ratio);
  const result = new Float32Array(newLength);

  for (let i = 0; i < newLength; i++) {
    const position = i * ratio;
    const index = Math.floor(position);
    const fraction = position - index;
    const s0 = samples[index] ?? 0;
    const s1 = samples[index + 1] ?? s0;
    result[i] = s0 + fraction * (s1 - s0);
  }

  return result;
}

/**
 * Encodes mono 16-bit PCM audio samples into a standard RIFF/WAVE Blob.
 * Produces a strictly valid 44-byte WAV header compatible with standard WAV parsers.
 */
export function encodePcmToWav(
  samples: Float32Array,
  sampleRate: number = 16000
): Blob {
  const numChannels = 1;
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = samples.length * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  // RIFF chunk descriptor
  view.setUint8(0, 0x52); // 'R'
  view.setUint8(1, 0x49); // 'I'
  view.setUint8(2, 0x46); // 'F'
  view.setUint8(3, 0x46); // 'F'
  view.setUint32(4, 36 + dataSize, true);
  view.setUint8(8, 0x57); // 'W'
  view.setUint8(9, 0x41); // 'A'
  view.setUint8(10, 0x56); // 'V'
  view.setUint8(11, 0x45); // 'E'

  // "fmt " sub-chunk
  view.setUint8(12, 0x66); // 'f'
  view.setUint8(13, 0x6d); // 'm'
  view.setUint8(14, 0x74); // 't'
  view.setUint8(15, 0x20); // ' '
  view.setUint32(16, 16, true); // Subchunk1Size (16 for PCM)
  view.setUint16(20, 1, true); // AudioFormat (1 for PCM)
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true); // BitsPerSample

  // "data" sub-chunk
  view.setUint8(36, 0x64); // 'd'
  view.setUint8(37, 0x61); // 'a'
  view.setUint8(38, 0x74); // 't'
  view.setUint8(39, 0x61); // 'a'
  view.setUint32(40, dataSize, true);

  // Write 16-bit linear PCM samples with clipping
  let offset = 44;
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }

  return new Blob([view], { type: "audio/wav" });
}

export interface PcmRecorder {
  start: () => void;
  stop: () => void;
  clear: () => void;
  getSamples: () => Float32Array;
  dispose: () => void;
}

/**
 * Creates a direct PCM recorder attached to a MediaStream. Captures raw audio
 * samples in memory via Web Audio without needing MediaRecorder container transcoding.
 */
export function createPcmRecorder(
  stream: MediaStream,
  audioContext?: AudioContext
): PcmRecorder | null {
  const AudioCtx =
    typeof window !== "undefined"
      ? window.AudioContext || (window as any).webkitAudioContext
      : undefined;
  if (!AudioCtx) return null;

  try {
    const ctx = audioContext || new AudioCtx();
    const source = ctx.createMediaStreamSource(stream);
    const processor =
      typeof ctx.createScriptProcessor === "function"
        ? ctx.createScriptProcessor(4096, 1, 1)
        : null;
    if (!processor) return null;

    let chunks: Float32Array[] = [];
    let isRecording = false;

    processor.onaudioprocess = (e: AudioProcessingEvent) => {
      if (!isRecording) return;
      const input = e.inputBuffer.getChannelData(0);
      chunks.push(new Float32Array(input));
    };

    source.connect(processor);
    const muteGain = ctx.createGain();
    muteGain.gain.value = 0;
    processor.connect(muteGain);
    muteGain.connect(ctx.destination);

    return {
      start: () => {
        isRecording = true;
      },
      stop: () => {
        isRecording = false;
      },
      clear: () => {
        chunks = [];
      },
      getSamples: () => {
        if (chunks.length === 0) return new Float32Array(0);
        let totalLen = 0;
        for (let i = 0; i < chunks.length; i++) totalLen += chunks[i].length;
        const merged = new Float32Array(totalLen);
        let offset = 0;
        for (let i = 0; i < chunks.length; i++) {
          merged.set(chunks[i], offset);
          offset += chunks[i].length;
        }
        return resamplePcm(merged, ctx.sampleRate, 16000);
      },
      dispose: () => {
        isRecording = false;
        chunks = [];
        try {
          processor.disconnect();
          source.disconnect();
          muteGain.disconnect();
        } catch {}
      },
    };
  } catch {
    return null;
  }
}

/**
 * Converts an audio ArrayBuffer to a 16 kHz WAV Blob suitable for
 * speech-to-text transcription (POST /speech/transcribe).
 *
 * Checks for existing WAV headers first to avoid redundant transcoding.
 * If decoding is required, decodes via Web Audio, resamples to 16 kHz,
 * and serializes PCM WAV.
 */
export async function convertToWav(audioBuffer: ArrayBuffer): Promise<Blob> {
  if (isWavAudio(audioBuffer)) {
    return new Blob([audioBuffer], { type: "audio/wav" });
  }

  const AudioCtx = (
    typeof window !== "undefined"
      ? window.AudioContext || (window as any).webkitAudioContext
      : undefined
  ) as typeof AudioContext | undefined;

  if (!AudioCtx || !audioBuffer || audioBuffer.byteLength < 44) {
    throw new Error("Audio buffer is empty or unsupported.");
  }

  const ctx = new AudioCtx();
  try {
    let decoded: AudioBuffer;
    try {
      decoded = await ctx.decodeAudioData(audioBuffer.slice(0));
    } catch (decodeErr) {
      throw new Error("Unable to decode audio container into WAV format.");
    }

    const TARGET_SAMPLE_RATE = 16000;
    let finalBuffer: AudioBuffer = decoded;

    if (decoded.sampleRate !== TARGET_SAMPLE_RATE) {
      const offline = new (window as any).OfflineAudioContext(
        decoded.numberOfChannels,
        Math.ceil(decoded.duration * TARGET_SAMPLE_RATE),
        TARGET_SAMPLE_RATE
      );
      const src = offline.createBufferSource();
      src.buffer = decoded;
      src.connect(offline.destination);
      src.start(0);
      finalBuffer = await offline.startRendering();
    }

    const channelData: Float32Array[] = [];
    for (let ch = 0; ch < finalBuffer.numberOfChannels; ch++) {
      channelData.push(finalBuffer.getChannelData(ch));
    }

    const encode =
      (wavEncoder && (wavEncoder.encode || (wavEncoder as any).default?.encode)) ||
      (wavEncoder as any).encode;
    if (typeof encode === "function") {
      const wavArrayBuffer: ArrayBuffer = await encode({
        sampleRate: finalBuffer.sampleRate,
        channelData,
      } as any);
      return new Blob([new Uint8Array(wavArrayBuffer)], { type: "audio/wav" });
    }

    // Fallback if wavEncoder is unavailable: encode mono PCM directly
    return encodePcmToWav(channelData[0] || new Float32Array(0), TARGET_SAMPLE_RATE);
  } finally {
    try {
      ctx.close?.();
    } catch {}
  }
}
